import { z } from 'zod';
import { optionTypeSchema } from '../../../helper';
import { marketSchema, IMarket } from './market';
import { IRegion } from '../region/region';
import { IOptionType } from '@zz2/zz2-ui';

/**
 * Schema for validating market form data.
 * Extends the base market schema with form-specific validations.
 *
 * @property {string} code - Market code (max 5 characters)
 * @property {string} name - Market name (max 255 characters)
 * @property {string} channel - Market channel type ('L', 'E', or 'R')
 * @property {OptionType} [region] - Optional region selection with label/value pair
 */
export const marketFormSchema = marketSchema.extend({
    region: optionTypeSchema.optional(),
    channel: optionTypeSchema.optional(),
}).omit({
    regionId: true,
});

/**
 * Default schema for market form initialization.
 * Extends the form schema with form-specific fields and defaults.
 * Makes nonempty string fields optional with defaults.
 *
 * @property {string} code - Optional market code
 * @property {string} name - Optional market name
 */
export const marketDefaultFormSchema = marketFormSchema.extend({
    code: z.string().optional().default(''),
    name: z.string().optional().default(''),
});

/**
 * TypeScript interface for market form values.
 * Generated from the market form schema for type safety.
 */
export type IMarketFormValues = z.infer<typeof marketFormSchema>;

/**
 * Converts a Market model to form values for editing.
 * If no market is provided, returns default form values.
 *
 * @param {IMarket} [market] - Optional market model to convert
 * @param {Array<IRegion>} [regions] - Array of regions to use for mapping
 * @returns {IMarketFormValues} Form values ready for editing
 */
export const MarketFormValues = (market ?: IMarket, regions ?: Array<IRegion>) : IMarketFormValues => {
    if (!market) return marketDefaultFormSchema.parse({});

    const { regionId } = market;

    return marketFormSchema.parse({
        ...market,
        channel: market.channel ? { label: getChannelOption(market.channel)?.label, value: market.channel } : undefined,
        region: regionId && regions?.find(x => x.id === regionId)
            ? {
                label: regions.find(x => x.id === regionId)?.name ?? '',
                value: regionId,
            }
            : undefined,
    });
};

/**
 * Validates market form values against the schema.
 * Logs validation errors to console if validation fails.
 *
 * @param {IMarketFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation succeeds, false otherwise
 */
export const validateMarketForm = (formValues : IMarketFormValues) : boolean => {
    try {
        marketFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Market form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};

export const channelOptions : Array<IOptionType> = [
    { label: 'Local', value: 'L' },
    { label: 'Regional', value: 'R' },
    { label: 'Export', value: 'E' },
];

export const getChannelOption = (channel : 'L' | 'R' | 'E') => channelOptions.find(x => x.value === channel);

